import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivityFindInterface } from '@zaoblako/shared/modules/finance/dtos/activity/activity-find.dto';
import {
  ActivityFormDto,
  ActivityFormInterface
} from '@zaoblako/shared/modules/finance/dtos/activity/activity-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { TypeOfActivityService } from '@zaoblako/web/core/services/ofd/type-of-activity.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-type-of-activity-form',
  templateUrl: './type-of-activity-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    FormsModule,
    JoinPipe,
    MultiSelectComponent,
    TagModule,
    ButtonComponent,
    SpinnerComponent,
    ContragentMultiSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TypeOfActivityFormComponent extends BaseFormComponent<ActivityFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public type: ActivityFindInterface = <ActivityFindInterface>{};
  public dto = ActivityFormDto;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    public readonly typeOfActivityService: TypeOfActivityService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.typeOfActivityService
      .findById<ActivityFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (type) => {
          this.type = type;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveType() {
    if (this.checkValidate(this.type).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.typeOfActivityService.update<ActivityFormInterface, ActivityFindInterface>(this.type).subscribe({
          next: (type) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Вид деятельности "${this.type.name}" обновлен!`);
            this.formStoreService.saveNext(type, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.typeOfActivityService.create<ActivityFindInterface>(this.type).subscribe({
          next: (type) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Вид деятельности создан!');
            this.formStoreService.saveNext(type, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteType() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить вид деятельности "${this.type.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.typeOfActivityService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Вид деятельности "${this.type.name}" удален!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreType() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить вид деятельности "${this.type.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.typeOfActivityService.restoreOne(this.type.id).subscribe({
          next: () => {
            this.type = { ...this.type, deleted: null };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Вид деятельности "${this.type.name}" восстановлен!`);
            this.formStoreService.saveNext(this.type, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
